<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useConfigurableInsightsStore } from '@/V2/stores/pinia/decision-engine/configurable-insights.store';
import InsightLogicSection from '@/V2/decision-engine/configurable-insights/components/InsightLogicSection.vue';
import InsightSettingsSection from '@/V2/decision-engine/configurable-insights/components/InsightSettingsSection.vue';
import InsightTypeSelector from '@/V2/decision-engine/configurable-insights/components/InsightTypeSelector.vue';
import InsightBackTestPreview from '@/V2/decision-engine/configurable-insights/components/InsightBackTestPreview.vue';
import {
  ConfigurableInsightLayoutType,
  type ConfigurableInsight,
} from '@/types/decision-engine/configurable-insights.types';
import DecisionEngineNav from '@/V2/decision-engine/DecisionEngineNav.vue';
import { showToast } from '@inscribe/signal';
import { validateInsightConfiguration } from '@/utils/decision-engine/configurable-insights';

// Ref for accessing the InsightSettingsSection component
const insightSettingsSectionRef =
  ref<InstanceType<typeof InsightSettingsSection>>();

const router = useRouter();
const vuexStore = useStore();
const store = useConfigurableInsightsStore();

const insight = ref<ConfigurableInsight>({
  signal_name: '',
  configuration: {
    enabled: true,
    impacts_risk: false,
    description: '',
    conditions: [],
  },
});

onMounted(() => {
  if (!vuexStore.getters.configurableRiskSignalsEnabled) {
    router.push({ name: 'decision-engine-detectors' });
    return;
  }

  const storeInsight = store.getCurrentInsight;
  if (storeInsight) {
    insight.value = {
      signal_name: storeInsight.signal_name,
      uuid: storeInsight.uuid,
      configuration: storeInsight.configuration,
      limitations: storeInsight.limitations,
    };
  }
});

// UI state
const isEditMode = computed(() => !!store.getCurrentInsight);
const insightType = ref<ConfigurableInsightLayoutType>(
  isEditMode.value
    ? ConfigurableInsightLayoutType.FLAG_COMBINED
    : ConfigurableInsightLayoutType.FLAG_TRANSACTION_ONLY,
);
const isSaving = ref(false);

// handlers
const onBackButtonClick = () => {
  router.push({ name: 'decision-engine-detectors', query: { view: 'custom' } });
};

// computed
const validationResult = computed(() => {
  return validateInsightConfiguration(insight.value);
});

const isInsightValid = computed(() => validationResult.value.isValid);

const onNavButtonClick = async () => {
  // Trigger validation in settings section
  insightSettingsSectionRef.value?.attemptSave();

  // Validate before saving
  if (!isInsightValid.value) {
    // Show validation errors as toast
    const errorMessage = validationResult.value.errors.join('\n');
    showToast({
      text: `Please fix the following errors:\n${errorMessage}`,
      isError: true,
    });
    return;
  }

  isSaving.value = true;

  try {
    const insightToSave: ConfigurableInsight = {
      ...(insight.value.uuid ? { uuid: insight.value.uuid } : {}),
      signal_name: insight.value.signal_name,
      configuration: insight.value.configuration,
    };

    if (insight.value.uuid) {
      await store.updateConfigurableInsight(insightToSave);
    } else {
      await store.saveConfigurableInsight(insightToSave);
    }

    // Success toast
    showToast({
      text: 'Insight successfully saved',
    });

    // Redirect to detectors page
    router.push({
      name: 'decision-engine-detectors',
      query: { view: 'custom' },
    });
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.message || 'Failed to save insight';
    showToast({
      text: errorMessage,
      isError: true,
    });
  } finally {
    isSaving.value = false;
  }
};

const onEnabledToggleChange = async (evt: boolean) => {
  insight.value.configuration.enabled = evt;

  // Only update in backend if editing an existing insight
  if (isEditMode.value) {
    try {
      await store.updateConfigurableInsight(insight.value);
      showToast({
        text: `Insight ${evt ? 'enabled' : 'disabled'} successfully`,
      });
    } catch (error: any) {
      // Revert on error
      insight.value.configuration.enabled = !evt;
      const errorMessage =
        error?.response?.data?.message || 'Failed to update insight';
      showToast({
        text: errorMessage,
        isError: true,
      });
    }
  }
};
</script>

<template>
  <div class="insight-edit-page d-flex flex-column">
    <decision-engine-nav
      class="edit-nav"
      category-name="Configurable Insights"
      :action-name="isEditMode ? 'Edit Insight' : 'New Insight'"
      :button-label="isSaving ? 'Saving...' : 'Save Insight'"
      :button-disabled="false"
      :item-name="
        insight.signal_name === '' ? 'Custom Insight' : insight.signal_name
      "
      :show-toggle="true"
      :show-delete-button="false"
      :toggle-value="insight.configuration.enabled"
      :name-editable="false"
      @on-back-button-click="onBackButtonClick"
      @on-button-click="onNavButtonClick"
      @on-enabled-toggle-change="onEnabledToggleChange"
    >
    </decision-engine-nav>

    <div class="insight-edit-container edit-container i-p-11">
      <div class="d-flex flex-column i-gap-11 content">
        <InsightTypeSelector v-model="insightType" :disabled="isEditMode" />

        <InsightSettingsSection
          ref="insightSettingsSectionRef"
          :insight="insight"
          class="content-card"
        />

        <InsightLogicSection
          :insight-type="insightType"
          :insight="insight"
          :is-edit-mode="isEditMode"
        />

        <InsightBackTestPreview :insight="insight" class="content-card" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.insight-edit-page {
  .insight-edit-container {
    width: 100%;

    .content {
      max-width: 1200px;
      margin-left: auto;
      margin-right: auto;
    }
  }
}
</style>
